import { useEffect, useState } from "react";
import api from "../api";
import { PrimaryButton } from "./ui/Form";

/**
 * Sign in with ArcGIS.
 *
 * The previous version of this card ran the out-of-band flow: it opened the
 * portal in a second tab, the user copied an approval code off that page, came
 * back, pasted it into a text box and pressed Submit. That existed only because
 * the backend's arcgis 1.9 had no way to accept a token directly, not because
 * anyone wanted it.
 *
 * Now the portal redirects back to /api/oauth/callback, the server does the
 * exchange, and the user lands on the app already signed in -- which is what
 * FieldSeeker does and what people expect.
 */
export default function LoginCard() {
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // The callback reports failures by bouncing back with ?login_error=...,
    // since by that point the browser is mid-redirect and there is no fetch
    // response left to put a message in.
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const err = params.get("login_error");
        if (!err) return;
        setError(err);
        // Drop it from the URL so a refresh does not resurrect a stale error.
        params.delete("login_error");
        const qs = params.toString();
        window.history.replaceState({}, "", window.location.pathname + (qs ? `?${qs}` : ""));
    }, []);

    const signIn = async () => {
        setBusy(true);
        setError(null);
        try {
            const res = await api.post("/start-login", {});
            // Same tab, not a popup: the portal redirects us back here, and a
            // popup would strand the session in a window the app cannot see.
            window.location.assign(res.data.oauth_url);
        } catch (err: any) {
            setError(err?.response?.data?.message ?? err.message ?? "Could not reach the server.");
            setBusy(false);
        }
    };

    return (
        <div className="card p-6 max-w-sm w-full text-center space-y-4">
            <div>
                <h2 className="font-extrabold text-lg">Sign in</h2>
                <p className="text-ink-soft text-[13px] mt-1">
                    Pioneer uses your CMAD ArcGIS account.
                </p>
            </div>

            {error && (
                <div className="rounded-sm2 bg-red-50 border border-red-200 p-3 text-left">
                    <p className="text-red-800 font-semibold text-sm">Sign-in failed</p>
                    <p className="text-red-700 text-[13px] mt-0.5">{error}</p>
                </div>
            )}

            <PrimaryButton
                onClick={signIn}
                busy={busy}
                busyLabel="Redirecting to ArcGIS…"
                className="w-full justify-center"
            >
                Sign in with ArcGIS
            </PrimaryButton>

            <p className="text-ink-faint text-[12px]">
                You will be sent to ArcGIS Online and returned here automatically.
            </p>
        </div>
    );
}
